/**
 * VH-83: Profile card for the user's available cooking equipment.
 *
 * Reads/writes /api/v1/profile/equipment. Booleans are simple checkboxes;
 * stovetop_plates is a 0-12 number; oven_max_temp_c is a number that's only
 * shown when oven is checked.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline'
import { useEquipmentConfig, useUpdateEquipment } from '../../hooks/useRecipes'

const BOOLEAN_FIELDS = [
  'oven',
  'microwave',
  'blender',
  'dough_mixer',
  'air_fryer',
  'pressure_cooker',
  'sous_vide',
  'teppanyaki',
]

const DEFAULT_EQUIPMENT = {
  teppanyaki: false,
  stovetop_plates: 0,
  oven: false,
  oven_max_temp_c: null,
  dough_mixer: false,
  blender: false,
  microwave: false,
  sous_vide: false,
  air_fryer: false,
  pressure_cooker: false,
}

export default function EquipmentSettingsCard() {
  const { t } = useTranslation()
  const { data, isLoading } = useEquipmentConfig()
  const updateMutation = useUpdateEquipment()
  const [form, setForm] = useState(DEFAULT_EQUIPMENT)

  useEffect(() => {
    if (data) {
      setForm({ ...DEFAULT_EQUIPMENT, ...data })
    }
  }, [data])

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        ...form,
        oven_max_temp_c: form.oven ? form.oven_max_temp_c : null,
      })
      toast.success(t('recipes.equipment.saved'))
    } catch (err) {
       
      console.error('[EquipmentSettingsCard] save failed', err)
      toast.error(t('recipes.equipment.saved'))
    }
  }

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            {t('recipes.equipment.title')}
          </h3>
        </div>
        <div className="card-body">
          <div className="animate-pulse h-24 bg-gray-100 rounded" />
        </div>
      </div>
    )
  }

  return (
    <div className="card" data-testid="equipment-settings-card">
      <div className="card-header flex items-center gap-2">
        <WrenchScrewdriverIcon className="h-5 w-5 text-primary-600" />
        <h3 className="text-lg font-medium text-gray-900">
          {t('recipes.equipment.title')}
        </h3>
      </div>
      <div className="card-body space-y-4">
        <p className="text-sm text-gray-600">
          {t('recipes.equipment.subtitle')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="flex items-center justify-between gap-2 p-2 rounded border border-gray-200">
            <span className="text-sm text-gray-700">
              {t('recipes.equipment.stovetopPlatesField')}
            </span>
            <input
              type="number"
              min="0"
              max="12"
              value={form.stovetop_plates ?? 0}
              onChange={(e) =>
                setForm({
                  ...form,
                  stovetop_plates: parseInt(e.target.value, 10) || 0,
                })
              }
              className="form-input w-20 text-sm"
              data-testid="equipment-stovetop-plates"
            />
          </label>

          {BOOLEAN_FIELDS.map((key) => (
            <label
              key={key}
              className="flex items-center justify-between gap-2 p-2 rounded border border-gray-200"
            >
              <span className="text-sm text-gray-700">
                {t(`recipes.equipment.labels.${key}`)}
              </span>
              <input
                type="checkbox"
                checked={Boolean(form[key])}
                onChange={(e) =>
                  setForm({ ...form, [key]: e.target.checked })
                }
                className="text-primary-600 focus:ring-primary-500"
                data-testid={`equipment-${key}`}
              />
            </label>
          ))}
        </div>

        {form.oven && (
          <div>
            <label className="form-label">
              {t('recipes.equipment.ovenMaxTempField')}
            </label>
            <input
              type="number"
              min="50"
              max="500"
              value={form.oven_max_temp_c ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  oven_max_temp_c:
                    e.target.value === ''
                      ? null
                      : parseInt(e.target.value, 10),
                })
              }
              className="form-input w-32"
              data-testid="equipment-oven-max-temp"
            />
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="btn-primary"
            data-testid="equipment-save"
          >
            {updateMutation.isPending
              ? t('recipes.equipment.saving')
              : t('recipes.equipment.save')}
          </button>
        </div>
      </div>
    </div>
  )
}
